import React from "react";
import { XMarkIcon } from "@heroicons/react/20/solid";

// Diálogo de confirmación con el look de los wizards (ClientesWizard,
// ComisionDialog): overlay, panel redondeado, encabezado con el icono en
// verde y pie con el botón primario en ACCENT. Sustituye a los <Alert> del
// template en las confirmaciones que muestran cifras.
//
// `tono` del botón primario: "verde" (default) o "rojo" para lo que borra.
// `children` es el cuerpo; `Resumen`/`Fila` (abajo) ayudan a listar montos.

export const ACCENT = "#1b4d2e";

const DialogoConfirmacion = ({
  open,
  onClose,
  titulo,
  subtitulo = null,
  icono: Icono = null,
  children,
  confirmar,
  cancelarLabel = "Volver",
  saving = false,
  maxWidth = "max-w-lg",
}) => {
  if (!open) return null;
  const tono = confirmar?.tono || "verde";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={saving ? undefined : onClose} />
      <div
        className={`relative w-full ${maxWidth} rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900 flex flex-col max-h-[92vh]`}
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/60">
          {Icono && (
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: ACCENT + "1a" }}
            >
              <Icono className="h-5 w-5" style={{ color: ACCENT }} />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-zinc-900 dark:text-white">{titulo}</h2>
            {subtitulo && <p className="mt-0.5 text-xs text-zinc-500">{subtitulo}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-zinc-400 hover:text-zinc-600 disabled:opacity-50"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto text-sm text-zinc-700 dark:text-zinc-300 space-y-3">
          {children}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-zinc-200 dark:border-zinc-700/60">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-700 disabled:opacity-50"
          >
            {cancelarLabel}
          </button>
          {confirmar && (
            <button
              type="button"
              onClick={confirmar.onClick}
              disabled={saving || confirmar.disabled}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50 ${
                tono === "rojo" ? "bg-red-600" : ""
              }`}
              style={tono === "rojo" ? undefined : { backgroundColor: ACCENT }}
            >
              {saving ? confirmar.savingLabel || "Guardando…" : confirmar.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Tarjeta con renglones "qué pasa": punto de color, texto y monto a la
// derecha. `tono` del punto: verde | ambar | teal | zinc | rojo.
const PUNTO = {
  verde: "bg-emerald-500",
  ambar: "bg-amber-500",
  teal: "bg-teal-500",
  zinc: "bg-zinc-400",
  rojo: "bg-red-500",
};

export const Resumen = ({ titulo = null, children }) => (
  <div className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 bg-zinc-50/60 dark:bg-zinc-800/40 divide-y divide-zinc-200 dark:divide-zinc-700/60">
    {titulo && (
      <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-400">
        {titulo}
      </div>
    )}
    {children}
  </div>
);

export const Fila = ({ tono = "zinc", children, monto = null, nota = null }) => (
  <div className="flex items-start gap-3 px-3 py-2.5">
    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${PUNTO[tono] || PUNTO.zinc}`} />
    <div className="min-w-0 flex-1">
      <div className="text-sm text-zinc-800 dark:text-zinc-200">{children}</div>
      {nota && <div className="mt-0.5 text-xs text-zinc-500">{nota}</div>}
    </div>
    {monto != null && (
      <div className="shrink-0 text-sm font-semibold text-zinc-900 dark:text-white whitespace-nowrap">
        {monto}
      </div>
    )}
  </div>
);

export default DialogoConfirmacion;
